import { RUNTIME_PROBE, RUNTIME_REPLY, isRuntimeReply, runtimeError, runtimeUrl } from './protocol'
import type { OperationKind, RuntimeEndpoint, RuntimeOperation, RuntimeWorker, WorkerSnapshot } from './protocol'

/** DOM/React-free worker-side owner. Responses never await application work. */
export class WorkerDiagnostics {
  private operations = new Map<string, RuntimeOperation>()
  private children = new Map<string, { endpoint: RuntimeEndpoint; worker: RuntimeWorker; snapshot: WorkerSnapshot; listener: (event: MessageEvent) => void }>()
  private sequence = 0

  async run<T>(name: string, kind: OperationKind, fn: (task: { update: (detail: string) => void }) => Promise<T>): Promise<T> {
    const id = `task-${++this.sequence}`
    this.operations.set(id, { id, name, kind, status: 'running', startedAt: Date.now() })
    const finish = (error?: unknown) => {
      const operation = this.operations.get(id)
      if (operation) this.operations.set(id, { ...operation, status: error === undefined ? 'completed' : 'failed', endedAt: Date.now(), error: error === undefined ? undefined : runtimeError(error) })
      const ended = [...this.operations.values()].filter(value => value.endedAt !== undefined)
      for (const value of ended.slice(0, -20)) this.operations.delete(value.id)
    }
    try {
      const value = await fn({ update: detail => {
        const operation = this.operations.get(id)
        if (operation && operation.endedAt === undefined) this.operations.set(id, { ...operation, detail })
      } })
      finish()
      return value
    } catch (error) {
      finish(error === undefined ? new Error('Operation failed') : error)
      throw error
    }
  }

  snapshot(): WorkerSnapshot {
    const operations = [...this.operations.values()]
    const workers: RuntimeWorker[] = []
    for (const [id, child] of this.children) {
      const status = child.worker.checkedAt && (!child.worker.lastResponseAt || child.worker.lastResponseAt < child.worker.checkedAt) && Date.now() - child.worker.checkedAt > 3_000 ? 'unresponsive' : child.worker.status
      workers.push({ ...child.worker, status })
      operations.push(...child.snapshot.operations.map(value => ({ ...value, id: `${id}:${value.id}`, resourceId: id, parentId: value.parentId ? `${id}:${value.parentId}` : undefined })))
      workers.push(...child.snapshot.workers.map(value => ({ ...value, id: `${id}:${value.id}`, parentId: id })))
    }
    return { operations, workers }
  }

  attach(endpoint: RuntimeEndpoint): () => void {
    const listener = (event: MessageEvent<unknown>) => {
      const data = event.data as { type?: string; id?: string } | null
      if (data?.type !== RUNTIME_PROBE || typeof data.id !== 'string') return
      endpoint.postMessage({ type: RUNTIME_REPLY, id: data.id, snapshot: this.snapshot() })
      for (const [id, child] of this.children) {
        if (child.worker.checkedAt && (!child.worker.lastResponseAt || child.worker.lastResponseAt < child.worker.checkedAt) && Date.now() - child.worker.checkedAt < 3_000) continue
        child.worker.checkedAt = Date.now()
        child.endpoint.postMessage({ type: RUNTIME_PROBE, id })
      }
    }
    endpoint.addEventListener('message', listener)
    return () => endpoint.removeEventListener('message', listener)
  }

  observeChild(endpoint: RuntimeEndpoint, name: string): () => void {
    const id = `child-${++this.sequence}`
    const worker: RuntimeWorker = { id, name, kind: 'dedicated', status: 'unknown', startedAt: Date.now() }
    const child = { endpoint, worker, snapshot: { operations: [], workers: [] } as WorkerSnapshot, listener: (_event: MessageEvent) => {} }
    child.listener = event => {
      if (!isRuntimeReply(event.data) || event.data.id !== id) return
      child.worker = { ...child.worker, status: 'responsive', lastResponseAt: Date.now() }
      child.snapshot = event.data.snapshot
    }
    this.children.set(id, child)
    endpoint.addEventListener('message', child.listener)
    return () => { endpoint.removeEventListener('message', child.listener); this.children.delete(id) }
  }

  instrumentFetch(fetchFn: typeof fetch): typeof fetch {
    return Object.assign((input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      return this.run(`Fetch ${runtimeUrl(url)}`, 'network', async task => {
        task.update('Waiting for response headers')
        const response = await fetchFn(input, init)
        task.update(`HTTP ${response.status}; response body may still be loading`)
        return response
      })
    }, fetchFn)
  }
}

export const workerDiagnostics = new WorkerDiagnostics()
